import {
  COMPONENT_SCHEMAS,
  COMPOSITE_DEFINITIONS,
  COMPETITOR_ANALYSIS_CONTRACT,
  ComponentDataV01,
  CompositeAnswerV01,
  CompetitorAnalysisComponentId,
  CompetitorAnalysisQuestionId,
  getCompositeSectionDescriptors,
} from '../index';
import type { AdvisoryBriefDocumentInputV01 } from './types';

type CompetitorData = {
  [ComponentId in CompetitorAnalysisComponentId]: ComponentDataV01[ComponentId];
};

const PREPARED_DATA: CompetitorData = {
  decision: {
    decision: 'Concentrate on regulated mid-market workflows before expanding horizontally.',
    rationale:
      'The segment combines urgent compliance work, fragmented incumbent tooling, and a credible path to proprietary operating data. A broad launch would dilute the implementation advantage before it becomes durable.',
    options: [
      {
        option: 'Focused regulated segment',
        terms: 'Two workflows, three lighthouse customers, stage-gated expansion',
        recommended: 'Recommended',
      },
      {
        option: 'Broad horizontal platform',
        terms: 'Six workflows launched in parallel',
        recommended: 'Not recommended',
      },
      {
        option: 'Partner-led entry',
        terms: 'Distribution first; product depth deferred',
        recommended: 'Reserve option',
      },
    ],
    gates: [
      {
        gate: 'Workflow pull',
        threshold: 'Three design partners commit to a paid pilot',
        if_met: 'Fund the focused launch',
        if_missed: 'Revisit the segment',
        current_position: 'Two confirmed; one in diligence',
      },
      {
        gate: 'Implementation repeatability',
        threshold: 'Second deployment completes within six weeks',
        if_met: 'Scale the delivery pod',
        if_missed: 'Keep the offer services-led',
        current_position: 'Not yet established',
      },
    ],
    investment: 'EUR 1.2m over twelve months',
    forgone: ['A simultaneous enterprise launch', 'A generic self-serve motion'],
  },
  'action-priority': {
    rows: [
      {
        action: 'Secure the third lighthouse customer',
        reinforces: 'Workflow pull and referenceability',
        owner_or_horizon: 'Commercial lead · 30 days',
        priority: '01',
      },
      {
        action: 'Instrument implementation effort by workflow',
        reinforces: 'Repeatability and margin confidence',
        owner_or_horizon: 'Delivery lead · 45 days',
        priority: '02',
      },
      {
        action: 'Pre-negotiate the channel pilot',
        reinforces: 'A reversible partner option',
        owner_or_horizon: 'Partnerships · 60 days',
        priority: '03',
      },
    ],
  },
  'counter-position': {
    rows: [
      {
        assumption: 'Regulation creates budget rather than only administrative burden',
        support: 'Four of five prepared interviews tied spend to a dated compliance event',
        counter_evidence: 'One buyer expects the incumbent suite to absorb the requirement',
        status: 'Testable',
      },
      {
        assumption: 'Workflow data compounds into a switching cost',
        support: 'Historical decisions improve later recommendations',
        counter_evidence: 'Export requirements may keep the data portable',
        status: 'Not established',
      },
    ],
    flip_conditions: [
      'The third design partner will not pay for the focused workflow',
      'The second implementation still requires more than ten weeks',
    ],
    smallest_test: 'Run one paid, fixed-scope implementation with a six-week delivery gate.',
  },
  'options-matrix': {
    criteria: [
      { criterion: 'Customer urgency', weight: '30%' },
      { criterion: 'Right to win', weight: '30%' },
      { criterion: 'Repeatable delivery', weight: '25%' },
      { criterion: 'Capital efficiency', weight: '15%' },
    ],
    rows: [
      {
        option: 'Focused regulated segment',
        scores: 'Urgency 5 · Win 4 · Delivery 4 · Capital 4',
        total: '4.35 / 5',
        rank: '1',
      },
      {
        option: 'Partner-led entry',
        scores: 'Urgency 4 · Win 3 · Delivery 3 · Capital 5',
        total: '3.60 / 5',
        rank: '2',
      },
      {
        option: 'Broad horizontal platform',
        scores: 'Urgency 2 · Win 2 · Delivery 2 · Capital 2',
        total: '2.00 / 5',
        rank: '3',
      },
    ],
    shortlisting_criterion: 'Keep options that clear 3.5 and do not score below 3 on right to win.',
    excluded: [
      {
        option: 'Acquire a horizontal competitor',
        reason: 'Integration cost fails the twelve-month capital gate',
      },
    ],
    confidence_notes: ['Partner economics remain directional until one channel pilot is priced.'],
  },
  'sensitivity-plot': {
    variable: 'Paid design partners secured in the first six months',
    outcome: 'Expected payback period',
    rows: [
      { case: 'Downside', variable_value: '1 partner', outcome_value: 'More than 30 months' },
      { case: 'Gate', variable_value: '3 partners', outcome_value: '22 months' },
      { case: 'Base', variable_value: '5 partners', outcome_value: '17 months' },
      { case: 'Upside', variable_value: '8 partners', outcome_value: '12 months' },
    ],
    breakpoint: 'Below three paid design partners, the launch no longer clears the payback gate.',
  },
  'market-sizing': {
    basis: 'Annual software and implementation spend, 2026 EUR, prepared bottom-up estimate.',
    rows: [
      {
        candidate: 'Regulated mid-market operations',
        size: 'EUR 640m',
        growth: '12–15% annually',
        source: 'Prepared market review',
        source_date: 'July 2026',
      },
      {
        candidate: 'Enterprise transformation offices',
        size: 'EUR 1.4bn',
        growth: '7–9% annually',
        source: 'Prepared market review',
        source_date: 'July 2026',
      },
      {
        candidate: 'Horizontal knowledge work',
        size: 'EUR 4.8bn',
        growth: '5–7% annually',
        source: 'Prepared market review',
        source_date: 'July 2026',
      },
    ],
    divergences: ['The enterprise estimate includes services; the other candidates exclude them.'],
    position: [
      {
        candidate: 'Regulated mid-market operations',
        tam: 'EUR 640m',
        sam: 'EUR 180m',
        company_revenue: 'EUR 3.6m',
        share: '2.0% of SAM',
      },
    ],
    whitespace: [
      {
        pool: 'Cross-border compliance teams',
        size: 'EUR 95m',
        growth: '18% annually',
        why_underserved: 'Incumbents configure country-by-country rather than workflow-by-workflow',
      },
    ],
  },
  'portfolio-matrix': {
    x_axis: 'Market attractiveness',
    y_axis: 'Right to win',
    rows: [
      {
        item: 'Regulated mid-market workflows',
        x: 'High',
        y: 'High',
        size: 'EUR 180m SAM',
        note: 'Invest now',
      },
      {
        item: 'Enterprise transformation offices',
        x: 'High',
        y: 'Medium',
        size: 'EUR 310m SAM',
        note: 'Build references first',
      },
      {
        item: 'Horizontal knowledge work',
        x: 'Medium',
        y: 'Low',
        size: 'EUR 1.1bn SAM',
        note: 'Do not pursue yet',
      },
    ],
  },
  'durability-factors': {
    advantage: 'Workflow-specific operating data reinforced by implementation practice',
    mechanism:
      'Every completed case improves the recommendation library and shortens the next deployment in the same workflow.',
    customer_value: 'Faster decisions with an auditable record and fewer manual hand-offs.',
    buildable: 'partially',
    rows: [
      {
        barrier: 'Accumulated decision patterns',
        type: 'cannot',
        durable_until: 'Three to four years in the base case',
        note: 'Depends on maintaining permission to learn across deployments',
      },
      {
        barrier: 'Embedded implementation practice',
        type: 'will_not',
        durable_until: 'Two years',
        note: 'Horizontal competitors resist a services-heavy motion',
      },
      {
        barrier: 'Model access',
        type: 'neither',
        durable_until: 'Replicable now',
        note: 'Not a source of advantage',
      },
    ],
    choice_factors: [
      {
        factor: 'Auditability',
        weight: 'High',
        company: 'Strong',
        competitors: 'Mixed',
        source: 'Prepared buyer interviews',
      },
      {
        factor: 'Implementation time',
        weight: 'High',
        company: 'Medium',
        competitors: 'Medium',
        source: 'Prepared delivery review',
      },
    ],
    structure: [
      {
        force: 'Incumbent rivalry',
        rating: 'High',
        evidence: 'Three suites can bundle adjacent capability',
      },
      {
        force: 'Buyer power',
        rating: 'Medium',
        evidence: 'Urgency is high but procurement remains concentrated',
      },
    ],
  },
  'risk-matrix': {
    rows: [
      {
        risk: 'Incumbent bundles the workflow into an existing suite',
        likelihood: 'Medium',
        impact: 'High',
        mitigation: 'Lead on implementation speed and workflow depth',
        owner: 'Product strategy',
      },
      {
        risk: 'Implementation remains bespoke',
        likelihood: 'Medium',
        impact: 'High',
        mitigation: 'Stop expansion until the six-week gate is proven',
        owner: 'Delivery lead',
      },
      {
        risk: 'Regulatory timetable slips',
        likelihood: 'Low',
        impact: 'Medium',
        mitigation: 'Target workflows with an existing operating burden',
        owner: 'Commercial lead',
      },
    ],
  },
  'people-grid': {
    rows: [
      {
        role_or_capability: 'Regulated-workflow product lead',
        current_state: 'Covered part-time',
        gap: 'No single accountable owner',
        action: 'Assign before the third pilot starts',
      },
      {
        role_or_capability: 'Repeatable implementation design',
        current_state: 'Strong individual practice',
        gap: 'Not codified',
        action: 'Create the first deployment playbook',
      },
    ],
  },
  'evidence-table': {
    rows: [
      {
        claim: 'The focused segment has dated compliance-driven urgency',
        source: 'Prepared buyer interview synthesis',
        locator: 'Interview themes 2–4',
        status: 'Reviewed',
      },
      {
        claim: 'Implementation can fall below six weeks',
        source: 'Prepared delivery retrospective',
        locator: 'Deployments 5 and 7',
        status: 'Directional',
      },
    ],
    not_established: [
      'A repeatable channel acquisition cost',
      'Retention beyond the first renewal cycle',
    ],
  },
  'metric-strip': {
    rows: [
      {
        label: 'Priority segment',
        value: 'EUR 180m',
        unit: 'serviceable market',
        period: '2026 basis',
        source: 'Prepared market review',
      },
      {
        label: 'Launch gate',
        value: '3',
        unit: 'paid design partners',
        period: 'six months',
        source: 'Prepared decision contract',
      },
      {
        label: 'Delivery gate',
        value: '6 weeks',
        unit: 'second deployment',
        period: 'before expansion',
        source: 'Prepared delivery review',
      },
    ],
  },
  narrative: {
    body: 'The recommendation is intentionally narrow. It buys learning where customer urgency is already visible, while preserving the option to expand only after delivery becomes repeatable.\n\nThe evidence does **not** yet support a horizontal platform claim.',
  },
};

const QUESTION_COPY: Record<CompetitorAnalysisQuestionId, string> = {
  'strategic-synthesis': 'What strategic position should the company take now?',
  'competitors-attack-collaborate-acquire':
    'Should the company attack, collaborate with, or acquire around the competitive set?',
  'market-attractiveness':
    'Which market spaces are attractive enough to justify concentrated investment?',
  'right-to-win': 'Where does the company have a credible right to win?',
  'competitive-durability': 'Which advantages can remain durable as competitors respond?',
  'emerging-risks': 'What emerging risks could invalidate the position?',
};

const clone = <Value>(value: Value): Value => JSON.parse(JSON.stringify(value)) as Value;

const preparedAnswer = (questionId: CompetitorAnalysisQuestionId): CompositeAnswerV01 => {
  const mapping = COMPETITOR_ANALYSIS_CONTRACT.questions.find(
    question => question.questionId === questionId
  );
  if (!mapping) {
    throw new Error(`Prepared fixture question is not mapped: ${questionId}`);
  }
  const composite = COMPOSITE_DEFINITIONS[mapping.compositeId];
  const sections = getCompositeSectionDescriptors(composite).map(descriptor => ({
    ...descriptor,
    filledFrom: 'prepared-competitor-analysis-fixture',
    fields: COMPONENT_SCHEMAS[descriptor.componentId].attributes.map(attribute => attribute.name),
    data: PREPARED_DATA[descriptor.componentId as CompetitorAnalysisComponentId],
  }));

  return {
    compositeId: mapping.compositeId,
    version: 1,
    filledBy: 'prepared-competitor-analysis-fixture',
    primaryFilled: true,
    sections: clone(sections) as unknown as CompositeAnswerV01['sections'],
    omitted: [],
    slidePlans: clone(composite.slidePlans) as unknown as CompositeAnswerV01['slidePlans'],
  };
};

export const COMPETITOR_ANALYSIS_PREPARED_FIXTURE: AdvisoryBriefDocumentInputV01 = {
  engagementId: 'competitor-analysis',
  projectLabel: 'Prepared competitor analysis',
  title: 'Where should a workflow intelligence company compete?',
  subtitle:
    'A prepared strategy exercise on where to play, how to win, and what could make the position last.',
  prepared: true,
  answers: COMPETITOR_ANALYSIS_CONTRACT.readOrder.map(questionId => ({
    questionId,
    question: QUESTION_COPY[questionId],
    rendering: preparedAnswer(questionId),
  })),
};
